import React, { useState, useEffect, useCallback } from 'react';
import { getMyPasskeys, deleteMyPasskey } from '../services/api';
import { showSuccess, showError } from './ToastNotifications';
import { formatDate } from '../utils/helpers';
import PasskeyEnrol from './PasskeyEnrol';
import './TwoFactor.css';

// My account → Passkeys (administrators): the list, add another, remove one.
// Removing needs a passkey (server step-up); the last one can never go.
export default function PasskeyList({ user, onChange }) {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try { setRows((await getMyPasskeys()).passkeys || []); }
    catch (e) { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const isAdmin = !!user?.passkeyEligible; // the person holds an Administrator role (user identities, 2026-10-30)
  const remove = async (pk) => {
    if (!window.confirm(`Remove the passkey "${pk.name}"? Sign-ins and user-management changes from that device will need another passkey or your authenticator code.`)) return;
    setBusyId(pk.id);
    try {
      const r = await deleteMyPasskey(pk.id);
      showSuccess(`Passkey "${pk.name}" removed`);
      await load();
      if (onChange) onChange(r?.passkeyCount);
    } catch (err) {
      showError(err.response?.data?.error || 'Could not remove the passkey');
    } finally { setBusyId(null); }
  };

  return (
    <div>
      {rows === null ? <small className="tf-muted">Loading…</small>
        : rows.length === 0 ? <small className="form-text" style={{ display: 'block', marginBottom: 8 }}>No passkey on this account yet.</small>
          : (
            <ul className="pk-list">
              {rows.map((pk) => (
                <li key={pk.id} className="pk-row">
                  <div className="pk-text">
                    <span className="pk-name">{pk.name}</span>
                    <span className="pk-meta">{pk.description} · added {formatDate(pk.created_at)}{pk.last_used_at ? ` · last used ${formatDate(pk.last_used_at)}` : ' · never used'}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline btn-small"
                    onClick={() => remove(pk)}
                    disabled={busyId === pk.id || (isAdmin && rows.length <= 1)}
                    title={isAdmin && rows.length <= 1 ? 'Administrators must keep at least one passkey — add another first' : 'Remove this passkey (needs a passkey)'}
                  >
                    {busyId === pk.id ? 'Removing…' : 'Remove'}
                  </button>
                </li>
              ))}
            </ul>
          )}
      {adding ? (
        <PasskeyEnrol
          onDone={async (r) => { setAdding(false); showSuccess('Passkey added'); await load(); if (onChange) onChange(r?.passkeyCount); }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="btn btn-outline btn-small" onClick={() => setAdding(true)}>Add a passkey</button>
          <small className="form-text">Lost every passkey device? Another administrator presses Reset passkeys on your account in User Management.</small>
        </div>
      )}
    </div>
  );
}
